import { PRESETS, type AnalysisPreset } from "./analysis-presets";
import { StockfishWorker, type PositionEval, type StockfishSearchProgress } from "./stockfish-worker";
import type { EvaluationScore } from "@chessla/shared";

type Priority = "foreground" | "background";
/** How long an engine may take to answer "stop" before it is treated as hung and terminated. */
const STOP_GRACE_MS = 3_000;
type Job = { priority: Priority; interrupted: boolean; run: () => Promise<void> };

export const positionEvaluationScore = (evaluation: Pick<PositionEval, "cp" | "mate">): EvaluationScore => ({
  cp: evaluation.mate == null ? evaluation.cp : null,
  mate: evaluation.mate,
});

/** One worker and cache for interactive positions and interruptible study analysis. */
export class LiveEvalService {
  private worker: StockfishWorker | null = null;
  private initPromise: Promise<StockfishWorker> | null = null;
  private readonly cache = new Map<string, PositionEval>();
  private readonly queue: Job[] = [];
  private active: Job | null = null;

  constructor(private readonly preset: AnalysisPreset = PRESETS[0]) {}

  /** Terminates an engine (and its threads) and forgets it, so the next search starts a fresh one. */
  private discard(worker: StockfishWorker) {
    if (this.worker === worker) {
      this.worker = null;
      this.initPromise = null;
    }
    worker.destroy();
  }

  private async getWorker(): Promise<StockfishWorker> {
    if (this.worker?.isReady) return this.worker;
    // A dead engine still holds its threads and memory until terminated; never hand it out again.
    if (this.worker) this.discard(this.worker);
    this.initPromise ??= (async () => {
      const worker = new StockfishWorker();
      try {
        await worker.init(this.preset);
      } catch (error) {
        worker.destroy();
        this.initPromise = null;
        throw error;
      }
      this.worker = worker;
      return worker;
    })();
    return this.initPromise;
  }

  private async drain() {
    if (this.active) return;
    while (this.queue.length) {
      const foreground = this.queue.findIndex((job) => job.priority === "foreground");
      const [job] = this.queue.splice(foreground < 0 ? 0 : foreground, 1);
      this.active = job;
      job.interrupted = false;
      await job.run();
      this.active = null;
    }
  }

  private schedule<T>(
    priority: Priority,
    signal: AbortSignal | undefined,
    search: (worker: StockfishWorker, current: () => boolean) => Promise<T>,
  ): Promise<T | null> {
    if (signal?.aborted) return Promise.resolve(null);
    return new Promise((resolve) => {
      const job: Job = {
        priority,
        interrupted: false,
        run: async () => {
          const current = () => !signal?.aborted && !job.interrupted;
          try {
            if (signal?.aborted) return;
            const worker = await this.getWorker();
            if (!current()) return;
            let hung: ReturnType<typeof setTimeout> | undefined;
            const stop = () => {
              worker.stop();
              // An engine that ignores "stop" is hung or out of memory: terminate it so the queue moves on.
              hung = setTimeout(() => this.discard(worker), STOP_GRACE_MS);
            };
            signal?.addEventListener("abort", stop, { once: true });
            const result = await search(worker, current).finally(() => {
              clearTimeout(hung);
              signal?.removeEventListener("abort", stop);
            });
            if (current()) resolve(result);
          } catch {
            // A failed search remains unavailable; never manufacture an evaluation.
          } finally {
            if (job.interrupted && !signal?.aborted) this.queue.push(job);
            else resolve(null);
          }
        },
      };
      this.queue.push(job);
      if (priority === "foreground" && this.active?.priority === "background" && !this.active.interrupted) {
        this.active.interrupted = true;
        this.worker?.stop();
      }
      void this.drain();
    });
  }

  async analyze(
    fen: string,
    depth = PRESETS[0].depth,
    signal?: AbortSignal,
    onProgress?: (progress: StockfishSearchProgress) => void,
    multiPv = 1,
    priority: Priority = "foreground",
  ): Promise<PositionEval | null> {
    // Yield between background positions, including cached ones, so game grading cannot monopolize the UI task.
    if (priority === "background") await new Promise<void>((resolve) => setTimeout(resolve, 0));
    if (signal?.aborted) return null;
    const key = `${fen}@${depth}@${multiPv}`;
    const cached = () => {
      const exact = this.cache.get(key);
      if (exact) return exact;
      // Background confirmation searches must retain distinct requested depths.
      if (priority === "foreground")
        for (const [candidate, result] of this.cache)
          if (candidate.startsWith(`${fen}@`) && candidate.endsWith(`@${multiPv}`) && result.depth >= depth)
            return result;
      return null;
    };
    const existing = cached();
    if (existing) return existing;
    return this.schedule(priority, signal, async (worker, current) => {
      const queued = cached();
      if (queued) return queued;
      const result = await worker.evaluate(fen, depth, multiPv, (progress) => {
        if (current()) onProgress?.(progress);
        else worker.stop();
      });
      if (current() && result.depth >= depth) {
        this.cache.set(key, result);
        // Bounded session cache retains long studies without keeping every historical position forever.
        if (this.cache.size > 1024) this.cache.delete(this.cache.keys().next().value!);
      }
      return result;
    });
  }

  async chooseMoveAtElo(fen: string, rating: number, depth = PRESETS[0].depth, signal?: AbortSignal) {
    return this.schedule("foreground", signal, async (worker) => {
      const result = await worker.evaluateAtElo(fen, depth, rating, signal);
      return result.bestMove || null;
    });
  }

  async evaluate(fen: string, depth?: number): Promise<EvaluationScore | null> {
    const result = await this.analyze(fen, depth);
    return result ? positionEvaluationScore(result) : null;
  }
}

// Board must remain interactive even while a whole imported game is being analyzed.
export const liveEval = new LiveEvalService({ ...PRESETS[1], threads: 1 });
